import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  Vehicle,
  VehicleByCustomerID,
  VehicleCustomerList,
  VehicleList,
  VehicleListParams,
} from '@/libs/interfaces'
import { vehiclesApiMock } from '@/libs/mocks/handlers/vehicles'

const vehiclesApiReal = {
  create: async (payload: Partial<Vehicle>): Promise<CreateApiResult<Vehicle>> => {
    return api
      .post<ApiResponse<Vehicle>>(URLs.Vehicles.create(payload.orgID || ''), payload)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  delete: async (payload: Partial<Vehicle>): Promise<Vehicle> => {
    const { orgID = '', driverCarID = '' } = payload

    return api
      .delete<Vehicle>(URLs.Vehicles.delete(orgID, driverCarID))
      .then((response) => response.data)
  },
  getByCustomer: async (orgID: string, driverID: string): Promise<Vehicle[]> => {
    return await api
      .get<ApiResponse<VehicleByCustomerID>>(URLs.Vehicles.vehicleCustomerMap(orgID, driverID))
      .then((response) => {
        return response.data?.data?.driverCars || []
      })
  },
  getByID: async (orgID: string = '', driverCarID: string = ''): Promise<Vehicle> => {
    return await api
      .get<ApiResponse<Vehicle>>(URLs.Vehicles.byID(orgID, driverCarID))
      .then((response) => {
        return response.data.data || []
      })
  },
  getByRegNumber: async (
    orgID: string = '',
    driverCarRegistrationNumber: string = '',
  ): Promise<Vehicle> => {
    return await api
      .get<ApiResponse<Vehicle>>(URLs.Vehicles.byRegNumber(orgID, driverCarRegistrationNumber))
      .then((response) => {
        return response.data.data || []
      })
  },

  list: async (params?: VehicleListParams): Promise<VehicleCustomerList[]> => {
    const { orgID = '', ...queryParams } = params || {}
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<VehicleList>>(URLs.Vehicles.list(orgID, query))
      .then((response) => response.data?.data?.cars || [])
  },
  listCursor: async (
    params: VehicleListParams,
  ): Promise<CursorPaginatedResponse<VehicleCustomerList>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<VehicleList>>(URLs.Vehicles.list(orgID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.cars,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  update: async (payload: Partial<Vehicle>) => {
    const { orgID = '', driverCarID = '', ...patchData } = payload
    return await api
      .patch(URLs.Vehicles.byID(orgID, driverCarID), patchData)
      .then((response) => response.data)
  },
}

export const vehiclesApi: typeof vehiclesApiReal = __IS_MOCK_MODE__
  ? vehiclesApiMock
  : vehiclesApiReal
